import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, HostListener, Input, OnDestroy, Output } from '@angular/core';

export interface PaletteSubtitle {
  kind: '' | 'theme' | 'image' | 'harmony' | 'warm' | 'cool' | 'full' | 'custom' | 'named';
  themeName?: string;
  logo?: string;
  imageSrc?: string;
  imageName?: string;
  scheme?: string;
  name?: string;
  color?: string;
  // A CSS gradient string for the source swatch of a named ('named') gradient,
  // so the chip shows the actual featured colours blended rather than one dot.
  gradient?: string;
  seed?: string;
  // Clickable sources (open in a new tab): the theme page for a theme source,
  // the color page for a seed-color source.
  themeHref?: string;
  colorHref?: string;
}

// Sequential ids so two widgets on a page never share the file-input id.
let widgetSeq = 0;

// The interactive palette hero card, shared by the palette tool page and the
// home page. It owns the UI (tiles, drag, toolbar, Random menu, copy) but is
// otherwise controlled: it renders the `palette` it is given and emits intent
// events; the host decides what to do (navigate vs. keep local state).
@Component( {
  selector: 'app-palette-widget',
  templateUrl: './palette-widget.component.html',
  styleUrls: [ './palette-widget.component.scss' ],
} )
export class PaletteWidgetComponent implements OnDestroy {

  @Input() palette: { value: string }[] = [];
  @Input() subtitle: PaletteSubtitle | null = null;
  // The card heading.
  @Input() title = 'Palette Generator';
  @Input() showTiles = true;
  // How many colours an Initialize / From image action produces.
  @Input() count = 10;
  @Output() countChange = new EventEmitter<number>();
  // The colour-count floor: a default for most generators, overridden per scheme
  // (e.g. tetradic needs 4). The gradient tool passes a lower default (2) and its
  // own per-scheme overrides; the palette tool keeps the defaults below.
  @Input() defaultMin = 3;
  @Input() schemeMins: { [ key: string ]: number } = { 'split complementary': 3, triadic: 3, tetradic: 4 };
  // The palette tool's own layout (owner 2026-10-12): the source on top, the tiles across the card, the
  // selected colour's editor and the generator side by side in a tray under them. Home and the wallpaper tool
  // keep the classic card.
  @Input() studio = false;
  @Input() showDetailsCta = false;
  @Input() detailsRoute = '';
  @Input() detailsQueryParams: { [ key: string ]: string } | null = null;

  @Output() editColor = new EventEmitter<{ index: number; value: string }>();
  @Output() reorder = new EventEmitter<CdkDragDrop<unknown>>();
  @Output() addColor = new EventEmitter<void>();
  @Output() removeColor = new EventEmitter<number>();
  /** the palette in the opposite order (the host turns it around) */
  @Output() reverse = new EventEmitter<void>();
  @Output() generate = new EventEmitter<string>();
  @Output() uploadImage = new EventEmitter<Event>();

  showRandomButtons = false;
  readonly fileInputId = 'palette-image-upload-' + ( ++widgetSeq );

  // The tile the user has selected; its colour fills the editor panel below.
  activeIndex = 0;

  // The Initialize menu, in display order.
  readonly schemes: { key: string; label: string }[] = [
    { key: 'theme', label: 'Codigrate Themes' },
    { key: 'analogous', label: 'Analogous' },
    { key: 'monochrome', label: 'Monochrome' },
    { key: 'complementary', label: 'Complementary' },
    { key: 'split complementary', label: 'Split Complementary' },
    { key: 'triadic', label: 'Triadic' },
    { key: 'tetradic', label: 'Tetradic' },
    { key: 'warm', label: 'Warm' },
    { key: 'cool', label: 'Cool' },
    { key: 'full', label: 'Full Random' },
  ];

  // The selected tile, clamped to a valid index (the palette can shrink under it).
  get selIdx(): number {
    return Math.max( 0, Math.min( this.activeIndex, this.palette.length - 1 ) );
  }

  get selectedColor(): string {
    return this.palette[ this.selIdx ]?.value || '#000000';
  }

  // Hex shown in the editor's Color field, e.g. "#B4364A".
  get selectedHexLabel(): string {
    return '#' + this.selectedColor.replace( '#', '' ).toUpperCase();
  }

  // Single-click selects a tile (the editor below edits it).
  selectTile( index: number ): void {
    this.activeIndex = index;
  }

  // Turn the palette around; the same colour stays selected, at its new place.
  reverseColors(): void {
    if ( this.palette.length < 2 ) return;
    this.activeIndex = this.palette.length - 1 - this.selIdx;
    this.reverse.emit();
  }

  // Remove the selected colour (the host enforces the min count).
  removeSelected(): void {
    this.removeColor.emit( this.selIdx );
  }

  // While typing: only hex characters, kept behind a single '#', capped at 6.
  // No other characters and no shorthand — same as the old per-tile hex inputs.
  // As soon as it is a full, valid 6-digit hex, apply it live (don't wait for blur).
  onHexInput( el: HTMLInputElement ): void {
    const hex = ( el.value || '' ).replace( /[^0-9a-fA-F]/g, '' ).slice( 0, 6 );
    el.value = '#' + hex.toUpperCase();
    if ( /^[0-9a-fA-F]{6}$/.test( hex ) ) {
      this.editColor.emit( { index: this.selIdx, value: '#' + hex.toLowerCase() } );
    }
  }

  // Commit only a full 6-digit hex (no 3-digit shorthand); ignore anything else.
  commitSelectedHex( raw: string ): void {
    const v = ( raw || '' ).replace( /[^0-9a-fA-F]/g, '' );
    if ( /^[0-9a-fA-F]{6}$/.test( v ) ) {
      this.editColor.emit( { index: this.selIdx, value: '#' + v.toLowerCase() } );
    }
  }

  // Uploaded file name for the Source line, truncated the macOS Finder way: keep
  // the start AND the tail (so the extension stays visible) with a single middle
  // ellipsis, e.g. "my-super-extr…photo.jpeg" instead of a trail of dots.
  get imageNameDisplay(): string {
    const raw = ( this.subtitle?.imageName || '' ).trim();
    if ( !raw ) {
      return 'uploaded image';
    }
    const max = 24;
    if ( raw.length <= max ) {
      return raw;
    }
    const dot = raw.lastIndexOf( '.' );
    const ext = dot > 0 ? raw.slice( dot ) : '';            // ".jpeg" (with dot)
    const tailKeep = Math.min( raw.length - 1, ext.length + 5 );
    const headKeep = Math.max( 1, max - tailKeep - 1 );      // -1 for the ellipsis
    return raw.slice( 0, headKeep ) + '…' + raw.slice( raw.length - tailKeep );
  }

  // "a" vs "an" for the harmony subtitle ("Generated an analogous palette");
  // only "analogous" starts with a vowel sound among the scheme names.
  get schemeArticle(): string {
    return /^[aeiou]/i.test( ( this.subtitle?.scheme || '' ).trim() ) ? 'an' : 'a';
  }


  // a theme picked for Codigrate Themes ('' = any theme); the page presets it to the theme it shows
  selectedTheme = '';

  @Input() set themeKey( v: string | undefined ) {
    this.selectedTheme = v || '';
  }

  toggleRandom(): void {
    this.showRandomButtons = !this.showRandomButtons;
  }

  // Close the Random dropdown when clicking anywhere outside of it.
  @HostListener( 'document:click', [ '$event' ] )
  onDocumentClick( event: MouseEvent ): void {
    if ( !this.showRandomButtons ) {
      return;
    }
    const target = event.target as HTMLElement;
    if ( !target.closest( '.cp-palhero-rand' ) ) {
      this.showRandomButtons = false;
    }
  }

  pickScheme( key: string ): void {
    this.showRandomButtons = false;
    this.generate.emit( key );
  }

  // The source chosen in the Initialize dropdown (a scheme key or 'image').
  // Starts empty so the dropdown shows the "Select Generator" placeholder and
  // nothing generates until the user picks one.
  selectedScheme = '';

  // The page presets the dropdown to the current source generator (e.g. on a
  // random landing it reflects "Triadic" while the Range shows the generated
  // count). Driven by an input, but a later user pick still wins until the page's
  // own source actually changes (the input only re-fires when its value changes).
  @Input() set scheme( v: string ) {
    this.selectedScheme = v || '';
  }

  // The lowest colour count the current generator allows: a per-scheme override
  // (e.g. tetradic needs 4) falling back to the default floor.
  get countMin(): number {
    const m = this.schemeMins[ this.selectedScheme ];
    return m != null ? m : this.defaultMin;
  }

  // Codigrate Themes always yield a fixed 10-colour palette, so the count field
  // is locked to 10 while that source is selected.
  get countDisabled(): boolean {
    return this.selectedScheme === 'theme';
  }

  // Generate is only actionable once a generator is chosen.
  get canGenerate(): boolean {
    return !!this.selectedScheme;
  }

  // Options for the custom dropdown: every generator plus "From Image".
  get generatorOptions(): { value: string; label: string }[] {
    // Codigrate Themes first, From Image right after it, then the harmony schemes (owner, 2026-09-22)
    const [ first, ...rest ] = this.schemes.map( s => ( { value: s.key, label: s.label } ) );
    return [ first, { value: 'image', label: 'From Image' }, ...rest ];
  }

  // Apply each generator's count rule the moment it is picked: Themes lock to 10,
  // Tetradic floors at 4, the rest keep the user's count within 3-10.
  onSchemeChange( key: string ): void {
    this.selectedScheme = key;
    if ( key === 'theme' && this.count !== 10 ) {
      this.count = 10;
      this.countChange.emit( 10 );
    } else if ( this.count < this.countMin ) {
      this.count = this.countMin;
      this.countChange.emit( this.count );
    }
  }

  // Count field: digits only while typing.
  onCountInput( el: HTMLInputElement ): void {
    el.value = ( el.value || '' ).replace( /[^0-9]/g, '' ).slice( 0, 2 );
  }

  // Count field: clamp to the generator's min..10 on commit and notify the host.
  onCountCommit( el: HTMLInputElement ): void {
    const n = Math.max( this.countMin, Math.min( 10, parseInt( el.value, 10 ) || this.countMin ) );
    this.count = n;
    el.value = String( n );
    this.countChange.emit( n );
  }

  // The up / down stepper buttons inside the count field.
  stepCount( delta: number ): void {
    if ( this.countDisabled ) {
      return;
    }
    const n = Math.max( this.countMin, Math.min( 10, this.count + delta ) );
    if ( n !== this.count ) {
      this.count = n;
      this.countChange.emit( n );
    }
  }

  // Press-and-hold on a stepper button: step once immediately, then auto-repeat
  // after a short delay until the pointer is released (or the bound is reached).
  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  private holdRepeat: ReturnType<typeof setInterval> | null = null;

  startStep( delta: number ): void {
    this.stopStep();
    this.stepCount( delta );
    this.holdTimer = setTimeout( () => {
      this.holdRepeat = setInterval( () => {
        const before = this.count;
        this.stepCount( delta );
        if ( this.count === before ) {
          this.stopStep();
        }
      }, 90 );
    }, 350 );
  }

  stopStep(): void {
    if ( this.holdTimer ) {
      clearTimeout( this.holdTimer );
      this.holdTimer = null;
    }
    if ( this.holdRepeat ) {
      clearInterval( this.holdRepeat );
      this.holdRepeat = null;
    }
  }

  ngOnDestroy(): void {
    this.stopStep();
  }

  // The Generate button: run the selected source (a scheme, or the file picker
  // for "From image"). A no-op until a generator is chosen.
  onGenerateClick(): void {
    if ( !this.selectedScheme ) {
      return;
    }
    if ( this.selectedScheme === 'image' ) {
      const input = document.getElementById( this.fileInputId ) as HTMLInputElement | null;
      input?.click();
      return;
    }
    this.generate.emit( this.selectedScheme === 'theme' && this.selectedTheme ? 'theme:' + this.selectedTheme : this.selectedScheme );
  }

  // The editor's swatch picker: the host persists the change.
  onPick( index: number, value: string ): void {
    this.editColor.emit( { index, value } );
  }

}
